import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Loader,
  LoaderSizes,
  Pagination,
  Toggle,
} from "@hid-galaxy-ui/galaxy-react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import DeleteModal from "../../../../../../../common/components/DeleteModal";
import moment from "moment";
import { deleteExternalContractSagaAction } from "../../../../../../../redux/saga/external-contract/saga-actions";
import { usePartnerTabPermission } from "../../../../../../../contexts/PartnerPermissionContext";
import { useSessionCanEditModule } from "../../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../../utils/permissions/partnerManagementPermissionKeys";
import "./TabContentExternalContract.scss";
import { ContractType } from "../../../../../../../redux/slices/my-account/Types";
import ServiceContractDetailModal from "../../../../../../../common/components/ServiceContractDetailModal";

const TabContentExternalContract = () => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [showActiveOnly, setShowActiveOnly] = React.useState<boolean>(false);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { contracts, status } = useAppSelector((state) => state.my_account);
  const navigate = useNavigate();
  const isPartnerInactive = partnerDetails?.status === "Inactive";
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const canEditContracts = useSessionCanEditModule(PM_KEYS.CONTRACT_MANAGEMENT);
  const contractMutationsDisabled =
    tabReadOnly || !canEditContracts || isPartnerInactive;

  // Filter contracts based on toggle state
  const filteredContracts = React.useMemo(() => {
    if (showActiveOnly) {
      return contracts.filter((contract) => contract.status === "Active");
    }
    return contracts;
  }, [contracts, showActiveOnly]);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [showActiveOnly]);

  const handleToggleChange = (e: any) => {
    setShowActiveOnly(e.target.checked);
  };

  const pageSize = 5;
  const totalRecords = filteredContracts.length;

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const paginatedData = filteredContracts.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const contractStatus = (status: string) => {
    if (status === "Active") {
      return (
        <InlineStatus
          label="Active"
          appearance={InlineStatusAppearance.Positive}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (status === "Scheduled") {
      return (
        <InlineStatus
          label="Scheduled"
          appearance={InlineStatusAppearance.Caution}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else {
      return (
        <InlineStatus
          label={status}
          appearance={InlineStatusAppearance.Danger}
          iconType={InlineStatusIconType.Dot}
        />
      );
    }
  };

  return (
    <div className="tab-content-external-contract">
      {status.contractsLoading ? (
        <div className="loading">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : contracts.length > 0 ? (
        <div className="external-contract-list-container">
          <div className="external-contract-list-wrapper">
            <div className="toggle-container">
              <Toggle
                id="external-contract-status-toggle"
                labelON="Active Contracts"
                labelOFF="All Contracts"
                onClick={handleToggleChange}
                toggled={showActiveOnly}
              />
              <Button
                variant="primary"
                label="Create External Contract"
                icon={<Iconography icon="plus" />}
                onClick={() => {
                  navigate(
                    `/hid-admin/create-external-contract/${partnerDetails?.ID}`
                  );
                }}
                disabled={contractMutationsDisabled}
              />
            </div>
            {paginatedData.map((contract) => (
              <ManageContractCard
                key={contract.ID}
                contract={contract}
                contractStatus={contractStatus}
                contractMutationsDisabled={contractMutationsDisabled}
                contractEditDisabled={contractMutationsDisabled}
              />
            ))}
          </div>
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handlePageChange}
          />
        </div>
      ) : (
        <div className="no-external-contract">
          <Iconography icon="file" size={IconographySizes.Large} />
          <p>No External Contracts Created</p>
          <Button
            variant="primary"
            label="Create External Contract"
            icon={<Iconography icon="plus" />}
            onClick={() => {
              navigate(
                `/hid-admin/create-external-contract/${partnerDetails?.ID}`
              );
            }}
            disabled={contractMutationsDisabled}
          />
        </div>
      )}
    </div>
  );
};

const ManageContractCard: React.FC<{
  contract: ContractType;
  contractStatus: (status: string) => React.ReactElement;
  contractMutationsDisabled: boolean;
  contractEditDisabled: boolean;
}> = ({
  contract,
  contractStatus,
  contractMutationsDisabled,
  contractEditDisabled,
}) => {
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { status } = useAppSelector((state) => state.external_contract);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onDeleteButtonClick = () => {
    dispatch(
      deleteExternalContractSagaAction({
        externalContractID: contract.ID,
        setModalOpen: setDeleteOpen,
      })
    );
  };

  const isServiceContract = contract.source.toLowerCase() === "service";
  const deleteDisabled = contractMutationsDisabled || isServiceContract;

  const onEditButtonClick = () => {
    if (isServiceContract) {
      setEditModalOpen(true);
    } else {
      navigate(`/hid-admin/update-external-contract/${contract.ID}`);
    }
  };

  return (
    <>
      <div className="external-contract-card">
        <div className="info">
          {/* <div className="label-value">
            <div className="label">Company Name</div>
            <div className="value">{contract.company_name || partnerDetails?.partner_name}</div>
          </div> */}
          <div className="label-value">
            <div className="label">Status</div>
            <div className="status-value">
              {contractStatus(contract.status)}
            </div>
          </div>
          <div className="label-value">
            <div className="label">Start Date</div>
            <div className="value">
              {moment(contract.start_date)?.format("DD-MMM-yyyy")}
            </div>
          </div>
          <div className="label-value">
            <div className="label">Expiry Date</div>
            <div className="value">
              {moment(contract.end_date)?.format("DD-MMM-yyyy")}
            </div>
          </div>
          <div className="label-value">
            <div className="label">Work Package Type</div>
            <div className="value">{contract.contract_name}</div>
          </div>
        </div>
        <div className="button-group">
          <Button
            className={`edit-button${contractEditDisabled ? " edit-button--disabled" : ""}`}
            variant="secondary"
            icon={
              <Iconography
                icon="pen"
                size={IconographySizes.Medium}
                theme={
                  contractEditDisabled
                    ? IconographyThemes.Default
                    : IconographyThemes.Info
                }
              />
            }
            onClick={onEditButtonClick}
            disabled={contractEditDisabled}
          />
          <Button
            className={`delete-button${deleteDisabled ? " delete-button--disabled" : ""}`}
            variant="secondary"
            icon={
              <Iconography
                icon="trashCan"
                size={IconographySizes.Medium}
                theme={
                  deleteDisabled
                    ? IconographyThemes.Default
                    : IconographyThemes.Error
                }
              />
            }
            onClick={() => {
              setDeleteOpen(true);
            }}
            disabled={deleteDisabled}
          />
        </div>
      </div>
      {deleteOpen && (
        <DeleteModal
          setDeleteOpen={setDeleteOpen}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteButtonLoading={status.deleteExternalContractLoading}
          optionalHeader="Delete External Contract"
          message="This will delete the contract permanently."
        />
      )}
      {editModalOpen && (
        <ServiceContractDetailModal
          setDetailModalOpen={setEditModalOpen}
          serviceContractDetail={contract}
          isEditModal={true}
        />
      )}
    </>
  );
};

export default TabContentExternalContract;
